"use client";

import { useProfile } from "@/context/useProfileProps";
import dynamic from "next/dynamic";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type OtherAction = {
  notificationType: string;
  timestamp: number;
};
type NotificationInfo = {
  KIWI_COMPANY_ID?: string;
  KIWI_JOB_ID?: string;
  MESSAGE_CODE?: string;
  MESSAGE_ID?: string;
  OUTREACH_MAIL_ID?: string;
  COMPANY_NAME?: string;
  NAME?: string;
};

type ApplyReplyNotificationData = {
  reply: string | null;
  apply: string | null;
  messageId: string | null;
  messageCode: string | null;
  corpId: string | null;
  folderId: string | null;
  outreachMailEventId?: string;
};

type NotificationCardProps = {
  notification: {
    notificationId: string;
    notificationType: string;
    timestamp: number;
    company: {
      name: string;
      logoUrl: string;
    };
    otherActions?: {
      count: number;
      list: OtherAction[];
    };
    notificationInfo?: NotificationInfo;
  };
};

const ApplyReplyFlow = dynamic(() =>
  import("@monsterindia/features").then(
    (mod) =>
      mod.ApplyReplyFlow as unknown as React.FC<{
        isMobile: boolean;
        notificationData: ApplyReplyNotificationData;
        setNotificationData?: (
          param: null | ApplyReplyNotificationData,
        ) => void;
        source?: string;
      }>,
  ),
);

const VIEW_MESSAGE_NOTIFICATION_TYPES = [
  "PROFILE_CONTACTED",
  "PROFILE_CONTACTED_BY_FOLLOWED",
];

const actionTypeLabels: Record<string, string> = {
  PROFILE_VIEWED: "Profile Viewed",
  PROFILE_SAVED: "Profile Saved",
  DOWNLOADED_CV: "Resume Downloaded",
  PROFILE_CONTACT_VIEWED: "Contact Viewed",
  PROFILE_CONTACTED: "Contacted",
};

const getActionText = (type: string, companyName: string): React.ReactNode => {
  const Company = () => (
    <span className="text-sm font-bold leading-5 text-[#1D1934]">
      {companyName}
    </span>
  );
  const Action = ({ label }: { label: string }) => (
    <span className="text-sm font-bold leading-5 text-[#1D1934]">{label}</span>
  );

  const actionMap: Record<string, React.ReactNode> = {
    PROFILE_VIEWED: (
      <>
        Recruiter from <Company /> has <Action label="viewed" /> your profile!
      </>
    ),
    PROFILE_SAVED: (
      <>
        Recruiter from <Company /> has <Action label="saved" /> your profile!
      </>
    ),
    DOWNLOADED_CV: (
      <>
        Recruiter from <Company /> has <Action label="downloaded" /> your
        profile!
      </>
    ),
    PROFILE_CONTACT_VIEWED: (
      <>
        Recruiter from <Company /> has <Action label="viewed" /> your contact!
      </>
    ),
    PROFILE_CONTACTED: (
      <>
        Recruiter from <Company /> has <Action label="contacted" /> you!
      </>
    ),
  };

  return (
    actionMap[type] || (
      <>
        <Company /> - {type.replace(/_/g, " ")}
      </>
    )
  );
};

const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) {
    return seconds <= 1 ? "Just now" : `${seconds}s ago`;
  }
  if (minutes < 60) {
    return minutes === 1 ? "1 min ago" : `${minutes} min ago`;
  }
  if (hours < 24) {
    return hours === 1 ? "1 hr ago" : `${hours} hr ago`;
  }
  if (days === 1) {
    return "A day ago";
  }
  if (days < 7) {
    return `${days} days ago`;
  }
  if (weeks === 1) {
    return "A week ago";
  }
  if (days < 30) {
    return `${weeks} weeks ago`;
  }
  if (months === 1) {
    return "A month ago";
  }
  if (days < 365) {
    return `${months} months ago`;
  }
  if (years === 1) {
    return "A year ago";
  }
  return `${years} years ago`;
};

const OtherActionsModal: React.FC<{
  actions: OtherAction[];
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement>;
  position: { top: number; left: number };
  mounted: boolean;
}> = ({ actions, isOpen, onClose, anchorRef, position, mounted }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!mounted || !isOpen || actions.length === 0) return null;

  return createPortal(
    <div
      ref={modalRef}
      className="absolute z-50 min-w-[163px] max-w-[220px] rounded-xl border border-[#E8E8EB] bg-white py-[10px] shadow-[0px_4px_12px_rgba(48,48,48,0.16)]"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="flex flex-col gap-3">
        {actions.map((action, index) => (
          <div
            key={`${action.notificationType}-${action.timestamp}-${index}`}
            className="flex items-center px-4"
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium leading-4 text-[#17142A]">
                {actionTypeLabels[action.notificationType] ||
                  action.notificationType.replace(/_/g, " ")}
              </span>
              <span className="text-xs font-normal leading-4 text-[#777585]">
                {formatRelativeTime(action.timestamp)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
}) => {
  const { isMobile } = useProfile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [mounted, setMounted] = useState(false);
  const [modalPosition, setModalPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });

  const [applyReplyData, setApplyReplyData] =
    useState<ApplyReplyNotificationData | null>(null);

  const hasOtherActions =
    notification.otherActions &&
    notification.otherActions.count > 0 &&
    (notification.otherActions.list?.length || 0) > 0;

  const shouldShowViewMessage =
    VIEW_MESSAGE_NOTIFICATION_TYPES.includes(notification.notificationType) &&
    notification.notificationId &&
    notification.notificationInfo?.KIWI_COMPANY_ID &&
    notification.notificationInfo?.KIWI_JOB_ID &&
    notification.notificationInfo?.MESSAGE_ID &&
    notification.notificationInfo?.MESSAGE_CODE;

  const handleViewMessageClick = useCallback(() => {
    if (!notification.notificationInfo) return;

    const { notificationInfo } = notification;
    setApplyReplyData({
      corpId: notificationInfo.KIWI_COMPANY_ID || null,
      folderId: notificationInfo.KIWI_JOB_ID || null,
      messageCode: notificationInfo.MESSAGE_CODE || null,
      messageId: notificationInfo.MESSAGE_ID || null,
      outreachMailEventId: notificationInfo.OUTREACH_MAIL_ID || undefined,
      reply: "true",
      apply: null,
    });
  }, [notification]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updateModalPosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const maxWidth = 220;
    const padding = 8;
    const left = Math.min(
      rect.left + window.scrollX,
      viewportWidth + window.scrollX - maxWidth - padding,
    );
    const top = rect.bottom + window.scrollY + 8;
    setModalPosition({ top, left });
  };

  const handleOtherActionsClick = () => {
    setIsModalOpen((prev) => !prev);
    setTimeout(updateModalPosition, 0);
  };

  const otherActions = hasOtherActions && (
    <div className="relative mt-1">
      <button
        ref={buttonRef}
        onClick={handleOtherActionsClick}
        className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-left text-xs font-normal leading-4 text-[#6E00BE] transition-opacity hover:opacity-80"
      >
        <span>
          Other action
          {notification.otherActions && notification.otherActions.count > 1
            ? "s"
            : ""}{" "}
          ({notification.otherActions?.count})
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transition-transform duration-200 ${isModalOpen ? "rotate-90" : "rotate-0"}`}
        >
          <path
            d="M4.5 2.5L8 6L4.5 9.5"
            stroke="#7654C9"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <OtherActionsModal
        actions={notification.otherActions?.list || []}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        anchorRef={buttonRef}
        position={modalPosition}
        mounted={mounted}
      />
    </div>
  );

  const viewMessageCTA = shouldShowViewMessage && (
    <div className="relative mt-1">
      <button
        onClick={handleViewMessageClick}
        className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-left text-xs font-normal leading-4 text-[#6E00BE] transition-opacity hover:opacity-80"
      >
        <span>View Message</span>
      </button>
    </div>
  );

  useEffect(() => {
    if (!isModalOpen) return;
    updateModalPosition();

    const handleWindowEvents = () => {
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;

      const scrollableParent = buttonRef.current.closest(
        ".overflow-y-auto, .overflow-auto, [style*='overflow']",
      );
      if (scrollableParent) {
        const parentRect = scrollableParent.getBoundingClientRect();
        const isVisibleInParent =
          rect.top >= parentRect.top && rect.bottom <= parentRect.bottom;

        if (!isVisibleInParent) {
          setIsModalOpen(false);
          return;
        }
      }

      if (!isVisible) {
        setIsModalOpen(false);
        return;
      }

      updateModalPosition();
    };

    window.addEventListener("resize", handleWindowEvents);
    window.addEventListener("scroll", handleWindowEvents, true);
    return () => {
      window.removeEventListener("resize", handleWindowEvents);
      window.removeEventListener("scroll", handleWindowEvents, true);
    };
  }, [isModalOpen]);

  return (
    <>
      <div className="group relative flex items-start gap-3 bg-white py-3 transition-colors duration-150 hover:bg-[#F6F6F7] md:!px-4">
        <div className="relative flex-shrink-0 overflow-hidden">
          <img
            src={
              notification.company.logoUrl ||
              "https://media.foundit.in/trex/public/default/images/career-services/prep-ai/default-company.svg"
            }
            alt={notification.company.name}
            className="h-10 w-10 rounded bg-[#F6F6F7]"
          />
        </div>

        <div className="flex flex-1 flex-col gap-[12px] md:!gap-1">
          <div className="text-sm font-medium leading-5 text-[#17142A]">
            {getActionText(
              notification.notificationType,
              notification.company.name,
            )}
          </div>

          {!isMobile ? (
            <div className="flex flex-col gap-1">
              <div className="text-xs leading-5 text-[#777585]">
                {formatRelativeTime(notification.timestamp)}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {viewMessageCTA}
              {otherActions}
            </div>
          )}
        </div>

        {isMobile ? (
          <div className="text-xs leading-5 text-[#777585]">
            {formatRelativeTime(notification.timestamp)}
          </div>
        ) : (
          <div
            className={`flex flex-row gap-2 transition-opacity duration-200 ${
              isModalOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
          >
            {viewMessageCTA}
            {otherActions}
          </div>
        )}
      </div>
      {applyReplyData && (
        <ApplyReplyFlow
          isMobile={isMobile}
          notificationData={applyReplyData}
          setNotificationData={setApplyReplyData}
          source="profile-performance-recruiter-actions"
        />
      )}
    </>
  );
};

export default memo(NotificationCard);
